/* eslint-disable no-restricted-syntax */
import i18n from 'i18next';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import { PageType } from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/data-structure/page-content';
import { BaseAnalyzer } from '../base/base-analyzer';
import { IReview, IParagraphReview, MachineReviewAnalyzer } from './machine-review-analyzer';
import { IRect } from '@src/slide/infrastructure/module/independent/graphic/rect/rect';
import { SlideInterface } from '@tencent/docs-api';

type IResult = SlideInterface.CheckResultItem;

interface IPageInfo {
    type: PageType;
    useLayout?: boolean;
}

const pageNames = {
    [PageType.CoverPage]: i18n.t('封面'),
    [PageType.TableOfContents]: i18n.t('目录'),
    [PageType.SectionDivider]: i18n.t('章节'),
    [PageType.Acknowledgments]: i18n.t('感谢'),
};

/**
 * 母版版式检查规则：
 * 1. 母版名称是否合规：风格+颜色
 * 2. 母版是否包含封面页、目录页、章节页、结束页
 */
export class MachineContentTypeAnalyzer extends BaseAnalyzer<IResult> {
    private title = i18n.t('AI模版规则检测');
    private description = i18n.t('检查模版是否符合AI生成的要求');
    private level = 'error' as const;

    public constructor(private reviewAnalyzer: MachineReviewAnalyzer) {
        super();
    }

    /**
     * 完全复用排版检查，但是内容推断并不适用于审核场景，之后换个算法
     * 因此先 copy 一下，也不抽象算法了
     */
    public async calculateResult(presentation: IPresentationModel): Promise<IResult> {
        const review = await this.reviewAnalyzer.result;
        const pageInfos = this.getPageInfos(review as IReview);
        const slideList = presentation.children;
        // 检查是否使用了对应版式
        slideList.forEach((slide, index) => {
            const pageType = pageInfos[index].type;
            if (pageType === PageType.Content) {
                return;
            }
            const layout = slide.getRelationLayoutModel();
            const layoutName = layout?.name;
            if (layoutName?.includes(pageNames[pageType])) {
                pageInfos[index].useLayout = true;
            } else {
                pageInfos[index].useLayout = false;
            }
        });

        // 重新组织结果用于输出
        const hasCover = pageInfos.some(page => page.type === PageType.CoverPage);
        const hasContents = pageInfos.some(page => page.type === PageType.TableOfContents);
        const hasSection = pageInfos.some(page => page.type === PageType.SectionDivider);
        const hasThanks = pageInfos.some(page => page.type === PageType.Acknowledgments);
        const passed = hasCover && hasContents && hasSection && hasThanks;
        if (passed) {
            return { passed, title: this.title, description: this.description };
        }
        const errorMessages: SlideInterface.CheckErrorMessage[] = [];
        if (!hasCover) {
            errorMessages.push({
                level: this.level,
                description: i18n.t('模版里不存在封面，请添加封面'),
            });
        }
        if (!hasContents) {
            errorMessages.push({
                level: this.level,
                description: i18n.t('模版里不存在目录，请添加目录'),
            });
        } else if (!hasSection) {
            errorMessages.push({
                level: this.level,
                description: i18n.t('模版里不存在章节页，请添加至少一页章节页'),
            });
        }
        if (!hasThanks) {
            errorMessages.push({
                level: this.level,
                description: i18n.t('模版里不存在致谢页，请添加致谢页'),
            });
        }
        return { passed, title: this.title, description: this.description, errorMessages };
    }

    private getPageInfos(review: IReview): IPageInfo[] {
        let contentsPageIndex;
        let sectionPageIndex;
        return review.pages.map((page, index) => {
            // 第一页认为是封面
            if (index === 0) {
                return { type: PageType.CoverPage };
            }
            // 最后两页有谢谢是感谢页
            const hasEaThank = str => str.text.replaceAll(/\s/g, '').includes('谢');
            const hasLatinThank = str => str.text.replaceAll(/\s/g, '').toLocaleLowerCase()
                .includes('thank');
            if (index >= review.pages.length - 1) {
                if (page.bodyTexts.some(i => hasEaThank(i) || hasLatinThank(i))) {
                    return { type: PageType.Acknowledgments };
                }
            }
            // 找目录页
            const hasEaContents = str => str.text.replaceAll(/\s/g, '').includes('目录');
            const hasLatinContents = str => str.text.replaceAll(/\s/g, '').toLocaleLowerCase()
                .includes('content');
            if (page.bodyTexts.some(i => hasEaContents(i) || hasLatinContents(i))) {
                contentsPageIndex = index;
                return { type: PageType.TableOfContents };
            }
            // 目录页后一个认为是章节页
            if (contentsPageIndex && contentsPageIndex + 1 === index) {
                sectionPageIndex = index;
                return { type: PageType.SectionDivider };
            }
            // 具有和第一个章节页相同结构的页也是章节页
            if (sectionPageIndex) {
                if (this.isSame(page.bodyTexts, review.pages[sectionPageIndex].bodyTexts)) {
                    return { type: PageType.SectionDivider };
                }
            }
            return { type: PageType.Content };
        });
    }

    // 对比 rect
    private isSame = (paragraphs1: IParagraphReview[], paragraphs2: IParagraphReview[]) => {
        if (paragraphs1.length !== paragraphs2.length) {
            return false;
        }
        for (let i = 0; i < paragraphs1.length; i += 1) {
            return this.isSameRect(paragraphs1[i].rect, paragraphs2[i].rect);
        }
    };

    // eslint-disable-next-line complexity
    private isSameRect(rect1?: IRect, rect2?: IRect, options: { width?: boolean; height?: boolean } = {}) {
        const { left: left1 = 0, width: width1 = 0 } = rect1 ?? {};
        const { top: top1 = 0, height: height1 = 0 } = rect1 ?? {};
        const { left: left2 = 0, width: width2 = 0 } = rect2 ?? {};
        const { top: top2 = 0, height: height2 = 0 } = rect2 ?? {};

        // 宽度相似
        if (options.width && Math.abs(width1 - width2) > 0.1 * width1) {
            return false;
        }
        // 高度相似
        if (options.height && Math.abs(height1 - height2) > 0.1 * height1) {
            return false;
        }
        // 上对齐
        if (Math.abs(top1 - top2) > 0.1 * height1) {
            return false;
        }
        // 左对齐
        if (Math.abs(left1 - left2) < 0.1 * width1) {
            return true;
        }
        // 右对齐
        if (Math.abs(left1 + width1 - (left2 + width2)) < 0.1 * width1) {
            return true;
        }
        // 居中对齐
        const widthHalf1 = width1 / 2;
        const widthHalf2 = width2 / 2;
        if (Math.abs(widthHalf1 + left1 - (widthHalf2 + left2)) < 0.1 * width1) {
            return true;
        }
        return false;
    }
}
